/** ルーティング（UI/UX設計書 §5 サイトマップ / §8 画面遷移）
 *  §14 コード分割：画面ごとに React.lazy で動的読込（3D は Three.js を含むため特に効果が大きい） */
import { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import Layout from "./components/Layout";
import RequireAuth from "./components/RequireAuth";
import { Skeleton } from "./components/ui";
import Top from "./pages/Top";                       // 初期表示は同期読込（LCP 優先）
import { ErrorPage } from "./pages/Misc";
import { PUBLIC_MODE, PUBLIC_TOP_PATH, isPublicSpace } from "./lib/publicMode";

const ProductList       = lazy(() => import("./pages/ProductList"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetail"));
const ShopDetailPage    = lazy(() => import("./pages/ShopDetail"));
const SpaceList         = lazy(() => import("./pages/SpaceList"));
const Space3D           = lazy(() => import("./pages/Space3D"));
const CartPage          = lazy(() => import("./pages/Cart"));
const Checkout          = lazy(() => import("./pages/Checkout"));
const MyPage            = lazy(() => import("./pages/MyPage"));
const OrderDetailPage   = lazy(() => import("./pages/MyPage").then((m) => ({ default: m.OrderDetailPage })));
const Notices           = lazy(() => import("./pages/Notices"));
const ShopAdmin         = lazy(() => import("./pages/ShopAdmin"));
const AdminConsole      = lazy(() => import("./pages/AdminConsole"));
const LoginPage         = lazy(() => import("./pages/Auth").then((m) => ({ default: m.LoginPage })));
const RegisterPage      = lazy(() => import("./pages/Auth").then((m) => ({ default: m.RegisterPage })));
const PasswordResetPage = lazy(() => import("./pages/Auth").then((m) => ({ default: m.PasswordResetPage })));
const LegalPage         = lazy(() => import("./pages/Misc").then((m) => ({ default: m.LegalPage })));

/** 画面遷移中のプレースホルダ（§15：スケルトンで表示） */
function PageFallback() {
  return (
    <div className="container stack" aria-busy="true" aria-label="読み込み中">
      <Skeleton height={32} width="40%" />
      <Skeleton height={240} radius={16} />
      <Skeleton height={120} radius={16} />
    </div>
  );
}

function ErrorRoute() {
  const { status } = useParams();
  return <ErrorPage status={Number(status) || 404} />;
}

/** 公開しているフロアだけを通す（それ以外はトップ＝最初のフロアへ戻す） */
function PublicSpaceRoute() {
  const { id } = useParams();
  if (!isPublicSpace(id)) return <Navigate to={PUBLIC_TOP_PATH} replace />;
  return (
    <RequireAuth>
      <Space3D />
    </RequireAuth>
  );
}

/**
 * 一般公開（研究デモ）用のルーティング。
 * 3D モールのフロアだけを残し、それ以外の URL はすべてトップへ寄せる。
 */
function PublicRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/spaces/:id" element={<PublicSpaceRoute />} />
          <Route path="*" element={<Navigate to={PUBLIC_TOP_PATH} replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  if (PUBLIC_MODE) return <PublicRoutes />;

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route element={<Layout />}>
          {/* Public */}
          <Route path="/" element={<Top />} />
          <Route path="/products" element={<ProductList />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/shops/:id" element={<ShopDetailPage />} />
          <Route path="/spaces" element={<SpaceList />} />
          <Route path="/legal/:doc" element={<LegalPage />} />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/password-reset" element={<PasswordResetPage />} />

          {/* User（認証必須） */}
          <Route path="/spaces/:id" element={<RequireAuth><Space3D /></RequireAuth>} />
          <Route path="/cart" element={<RequireAuth><CartPage /></RequireAuth>} />
          <Route path="/checkout" element={<RequireAuth><Checkout /></RequireAuth>} />
          <Route path="/mypage" element={<RequireAuth><MyPage /></RequireAuth>} />
          <Route path="/mypage/orders/:id" element={<RequireAuth><OrderDetailPage /></RequireAuth>} />
          <Route path="/notices" element={<RequireAuth><Notices /></RequireAuth>} />

          {/* Shop owner / Admin（ロール必須） */}
          <Route path="/shop" element={<RequireAuth roles={["shop_owner", "admin"]}><ShopAdmin /></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth roles={["admin"]}><AdminConsole /></RequireAuth>} />

          {/* System */}
          <Route path="/error/:status" element={<ErrorRoute />} />
          <Route path="/404" element={<ErrorPage status={404} />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
